import React, { useState } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { AdminPanelSettings, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthV2 } from '../../contexts/AuthContextV2';
import { BRAND_COLORS } from '../../styles/theme';
import { useTranslation } from '../../i18n';
import { isAdmin as checkIsAdmin, ROLE_OPTIONS } from '../../config/roles';
import {
  useAllOrganizationsQuery,
  useUnassignedUsersQuery,
  useOrganizationUsersQuery,
  useAllAnnouncementsAdminQuery,
} from './adminTools.queries';
import { InviteUserDialog } from './InviteUserDialog';
import { AddDroneDialog } from './dialogs/AddDroneDialog';
import { DroneAssignmentsDialog } from './dialogs/DroneAssignmentsDialog';
import { MaintenanceDocsDialog } from './dialogs/MaintenanceDocsDialog';
import { TenderEmailsDialog } from './dialogs/TenderEmailsDialog';
import { UserManagementDialog } from './dialogs/UserManagementDialog';
import { OrgManagementDialog } from './dialogs/OrgManagementDialog';
import { CreateOrgDialog } from './dialogs/CreateOrgDialog';
import { AnnouncementsDialog } from './dialogs/AnnouncementsDialog';
import { LegacyHoursMigrationDialog } from './dialogs/LegacyHoursMigrationDialog';
import { MaintenanceRecalculateDialog } from './dialogs/MaintenanceRecalculateDialog';
import { SoftwareReleasesDialog } from './dialogs/SoftwareReleasesDialog';
import { AdminToolsGrid } from './cards/AdminToolsGrid';
import { useAddDrone } from './hooks/useAddDrone';
import { useDroneAssignments } from './hooks/useDroneAssignments';
import { useMaintenanceDocuments } from './hooks/useMaintenanceDocuments';
import { useTenderEmails } from './hooks/useTenderEmails';
import { useUserManagement } from './hooks/useUserManagement';
import { useLegacyHoursMigration } from './hooks/useLegacyHoursMigration';
import { useMaintenanceRecalculate } from './hooks/useMaintenanceRecalculate';
import { useOrgManagement } from './hooks/useOrgManagement';
import { useAnnouncements } from './hooks/useAnnouncements';

export const AdminToolsPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuthV2();
  const { t } = useTranslation();

  // Derived early so TanStack Query `enabled` flags are correct on the first render.
  const isXerAdmin =
    (profile?.organization?.slug === 'xer-technologies' &&
      checkIsAdmin(profile?.organization?.roles)) ||
    false;
  const isAdmin = checkIsAdmin(profile?.organization?.roles);

  // Org management dialog + create-org state and handlers.
  const orgManagement = useOrgManagement();

  // TanStack Query hooks — org management / unassigned users (XerAdmin-only).
  // These are shared between the Org Management and Announcements dialogs, so they
  // live here in the parent and are passed down as props (no duplicate fetching).
  const orgsQuery = useAllOrganizationsQuery(isXerAdmin);
  const orgsData = orgsQuery.data ?? null;
  const orgsLoading = orgsQuery.isLoading;
  const orgsError = orgsQuery.isError ? (orgsQuery.error as Error).message : '';
  const unassignedUsersQuery = useUnassignedUsersQuery(isXerAdmin);
  const unassignedUsers = unassignedUsersQuery.data?.users ?? [];
  const unassignedUsersLoading = unassignedUsersQuery.isLoading;
  const orgUsersQuery = useOrganizationUsersQuery(orgManagement.expandedOrgId);
  const orgUsersQueryData = orgUsersQuery.data ?? null;
  const orgUsersLoading = orgUsersQuery.isLoading;
  const orgUsersError = orgUsersQuery.isError ? (orgUsersQuery.error as Error).message : '';

  // TanStack Query hooks — announcements (XerAdmin-only)
  const announcementsQuery = useAllAnnouncementsAdminQuery(isXerAdmin);
  const announcementsData = announcementsQuery.data ?? null;
  const announcementsLoading = announcementsQuery.isLoading;
  const announcementsError = announcementsQuery.isError
    ? (announcementsQuery.error as Error).message
    : '';

  // Announcements dialog + form state and handlers. orgsData is shared from above.
  const announcements = useAnnouncements({
    orgsData,
    refetchOrgs: async () => (await orgsQuery.refetch()).data ?? null,
  });

  // Launch-only tool hooks
  const userManagement = useUserManagement();
  const addDrone = useAddDrone();
  const droneAssignments = useDroneAssignments();
  const maintenanceDocs = useMaintenanceDocuments();
  const tenderEmails = useTenderEmails();
  const legacyHours = useLegacyHoursMigration();
  const maintenanceRecalculate = useMaintenanceRecalculate();

  // Invite User state
  const [inviteUserDialogOpen, setInviteUserDialogOpen] = useState(false);

  // Software Releases state
  const [softwareReleasesOpen, setSoftwareReleasesOpen] = useState(false);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }} data-testid="admin-tools-access-denied">
        <Typography variant="h5" color="error">
          {t('adminToolsPage.accessDenied')}
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('adminToolsPage.adminOnlyMessage')}
        </Typography>
        <Button onClick={() => navigate('/')} sx={{ mt: 2 }} variant="outlined">
          {t('adminToolsPage.returnToDashboard')}
        </Button>
      </Box>
    );
  }

  const handleOpenInviteDialog = () => {
    setInviteUserDialogOpen(true);
  };

  const handleCloseInviteDialog = () => {
    setInviteUserDialogOpen(false);
  };

  const handleInviteSent = async () => {
    await userManagement.refreshUsers();
  };

  // Map ROLE_OPTIONS to include translated labels — shared by Org Management
  // (assignment + role chips) and User Management.
  const roleDefinitions = ROLE_OPTIONS.map((role) => ({
    value: role.value,
    label: t(role.labelKey),
    description: t(role.descKey),
  }));

  return (
    <Box
      sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}
      data-testid="admin-tools-page"
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AdminPanelSettings sx={{ fontSize: 32, color: BRAND_COLORS.forestGreen.main }} />
              <Box>
                <Typography
                  variant="h4"
                  sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 600 }}
                >
                  {t('adminToolsPage.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('adminToolsPage.subtitle')}
                </Typography>
              </Box>
            </Box>
            <Button
              onClick={() => navigate('/')}
              startIcon={<ArrowBack />}
              variant="outlined"
              sx={{
                borderColor: BRAND_COLORS.yellow.main,
                color: BRAND_COLORS.yellow.main,
                '&:hover': {
                  borderColor: BRAND_COLORS.yellow.main,
                  backgroundColor: `${BRAND_COLORS.yellow.main}10`,
                },
              }}
            >
              {t('adminToolsPage.backToDashboard')}
            </Button>
          </Box>
        </Paper>

        {/* Tools Grid */}
        <AdminToolsGrid
          isXerAdmin={isXerAdmin}
          onOpenAddDrone={addDrone.openDialog}
          onOpenUserManagement={userManagement.openDialog}
          onOpenOrgManagement={orgManagement.openDialog}
          onOpenAnnouncements={announcements.openDialog}
          onOpenDroneAssignments={droneAssignments.openDialog}
          onOpenMaintenanceDocs={maintenanceDocs.openDialog}
          onOpenTenderEmails={tenderEmails.openDialog}
          onOpenLegacyHours={legacyHours.openDialog}
          onOpenMaintenanceRecalculate={maintenanceRecalculate.openDialog}
          onOpenSoftwareReleases={() => setSoftwareReleasesOpen(true)}
        />

        {/* Add Drone Dialog */}
        <AddDroneDialog
          open={addDrone.dialogOpen}
          onClose={addDrone.closeDialog}
          serialNumber={addDrone.serialNumber}
          model={addDrone.model}
          loading={addDrone.loading}
          result={addDrone.result}
          error={addDrone.error}
          onAddDrone={addDrone.addDrone}
          onSerialNumberChange={addDrone.setSerialNumber}
          onModelChange={addDrone.setModel}
        />

        {/* User Management Dialog */}
        <UserManagementDialog
          open={userManagement.dialogOpen}
          onClose={userManagement.closeDialog}
          data={userManagement.data}
          loading={userManagement.loading}
          error={userManagement.error}
          editingUserId={userManagement.editingUserId}
          editedRoles={userManagement.editedRoles}
          savingRoles={userManagement.savingRoles}
          onStartEdit={userManagement.startEdit}
          onCancelEdit={userManagement.cancelEdit}
          onToggleRole={userManagement.toggleRole}
          onSaveRoles={userManagement.saveRoles}
          onOpenInviteDialog={handleOpenInviteDialog}
          organizationName={profile?.organization?.name}
        />

        {/* Organization Management Dialog */}
        <OrgManagementDialog
          open={orgManagement.dialogOpen}
          onClose={orgManagement.closeDialog}
          orgsData={orgsData}
          orgsLoading={orgsLoading}
          orgsError={orgsError}
          unassignedUsers={unassignedUsers}
          unassignedUsersLoading={unassignedUsersLoading}
          unassignedUsersError={orgManagement.unassignedUsersError}
          expandedOrgId={orgManagement.expandedOrgId}
          orgUsersQueryData={orgUsersQueryData}
          orgUsersLoading={orgUsersLoading}
          orgUsersError={orgUsersError}
          assigningUserId={orgManagement.assigningUserId}
          selectedOrgForAssignment={orgManagement.selectedOrgForAssignment}
          selectedRolesForAssignment={orgManagement.selectedRolesForAssignment}
          roleDefinitions={roleDefinitions}
          onSelectOrgForAssignment={orgManagement.setSelectedOrgForAssignment}
          onToggleRoleForAssignment={orgManagement.toggleRoleForAssignment}
          onAssignUserToOrg={orgManagement.assignUserToOrg}
          onToggleOrgExpand={orgManagement.toggleOrgExpand}
          onOpenCreateOrg={orgManagement.openCreateOrgDialog}
        />

        {/* Create Organization Dialog */}
        <CreateOrgDialog
          open={orgManagement.createOrgDialogOpen}
          onClose={orgManagement.closeCreateOrgDialog}
          newOrgName={orgManagement.newOrgName}
          onNewOrgNameChange={orgManagement.setNewOrgName}
          result={orgManagement.createOrgResult}
          error={orgManagement.createOrgError}
          isPending={orgManagement.creatingOrg}
          onCreate={orgManagement.createOrganization}
        />

        {/* Drone Assignments Dialog */}
        <DroneAssignmentsDialog
          open={droneAssignments.dialogOpen}
          onClose={droneAssignments.closeDialog}
          dronesWithOrgs={droneAssignments.dronesWithOrgs}
          orgsData={droneAssignments.orgsData}
          loading={droneAssignments.loading}
          error={droneAssignments.error}
          editingSerial={droneAssignments.editingSerial}
          selectedOrg={droneAssignments.selectedOrg}
          updating={droneAssignments.updating}
          editingOwnershipSerial={droneAssignments.editingOwnershipSerial}
          selectedOwnershipType={droneAssignments.selectedOwnershipType}
          selectedLeaseEndDate={droneAssignments.selectedLeaseEndDate}
          updatingOwnership={droneAssignments.updatingOwnership}
          onStartEdit={droneAssignments.startEdit}
          onSaveOrg={droneAssignments.saveOrg}
          onCancelEdit={droneAssignments.cancelEdit}
          onSelectOrg={droneAssignments.setSelectedOrg}
          onStartOwnershipEdit={droneAssignments.startOwnershipEdit}
          onSaveOwnership={droneAssignments.saveOwnership}
          onCancelOwnershipEdit={droneAssignments.cancelOwnershipEdit}
          onSelectOwnershipType={droneAssignments.setSelectedOwnershipType}
          onSelectLeaseEndDate={droneAssignments.setSelectedLeaseEndDate}
        />

        {/* Maintenance Documents Dialog */}
        <MaintenanceDocsDialog
          open={maintenanceDocs.dialogOpen}
          onClose={maintenanceDocs.closeDialog}
          loading={maintenanceDocs.loading}
          docsData={maintenanceDocs.docsData}
          error={maintenanceDocs.error}
          selectedFile={maintenanceDocs.selectedFile}
          documentTitle={maintenanceDocs.documentTitle}
          documentType={maintenanceDocs.documentType}
          documentVersion={maintenanceDocs.documentVersion}
          droneModelFilter={maintenanceDocs.droneModelFilter}
          manufacturerFilter={maintenanceDocs.manufacturerFilter}
          uploading={maintenanceDocs.uploading}
          uploadResult={maintenanceDocs.uploadResult}
          uploadError={maintenanceDocs.uploadError}
          deletingDocKey={maintenanceDocs.deletingDocKey}
          processingDocKey={maintenanceDocs.processingDocKey}
          onFileSelect={maintenanceDocs.handleFileSelect}
          onUpload={maintenanceDocs.uploadDocument}
          onDelete={maintenanceDocs.deleteDocument}
          onProcess={maintenanceDocs.processDocument}
          onDocumentTitleChange={maintenanceDocs.setDocumentTitle}
          onDocumentTypeChange={maintenanceDocs.setDocumentType}
          onDocumentVersionChange={maintenanceDocs.setDocumentVersion}
          onDroneModelFilterChange={maintenanceDocs.setDroneModelFilter}
          onManufacturerFilterChange={maintenanceDocs.setManufacturerFilter}
        />

        {/* Announcements Dialog */}
        <AnnouncementsDialog
          open={announcements.dialogOpen}
          onClose={announcements.closeDialog}
          announcementsData={announcementsData}
          announcementsLoading={announcementsLoading}
          announcementsError={announcementsError}
          orgsData={orgsData}
          availableUsers={announcements.availableUsers}
          announcementTitle={announcements.title}
          announcementBody={announcements.body}
          announcementFiles={announcements.files}
          targetType={announcements.targetType}
          selectedOrgs={announcements.selectedOrgs}
          selectedUsers={announcements.selectedUsers}
          sendEmailNotification={announcements.sendEmailNotification}
          sendingAnnouncement={announcements.sending}
          sendAnnouncementResult={announcements.sendResult}
          sendAnnouncementError={announcements.sendError}
          onTitleChange={announcements.setTitle}
          onBodyChange={announcements.setBody}
          onFilesChange={announcements.handleFilesChange}
          onRemoveFile={announcements.removeFile}
          onTargetTypeChange={announcements.setTargetType}
          onSelectedOrgsChange={announcements.setSelectedOrgs}
          onSelectedUsersChange={announcements.setSelectedUsers}
          onSendEmailNotificationChange={announcements.setSendEmailNotification}
          onSend={announcements.send}
        />

        {/* Legacy Hours Migration Dialog */}
        <LegacyHoursMigrationDialog
          open={legacyHours.dialogOpen}
          onClose={legacyHours.closeDialog}
          activeTab={legacyHours.activeTab}
          onTabChange={legacyHours.handleTabChange}
          loading={legacyHours.loading}
          saving={legacyHours.saving}
          dronesData={legacyHours.dronesData}
          pilotsData={legacyHours.pilotsData}
          selectedDrone={legacyHours.selectedDrone}
          selectedPilot={legacyHours.selectedPilot}
          onSelectDrone={legacyHours.selectDrone}
          onSelectPilot={legacyHours.selectPilot}
          formState={legacyHours.formState}
          validationErrors={legacyHours.validationErrors}
          hasValidationErrors={legacyHours.hasValidationErrors}
          onPre2023Change={legacyHours.setPre2023}
          onYear2023Change={legacyHours.setYear2023}
          onYear2024Change={legacyHours.setYear2024}
          onSaveDroneHours={legacyHours.saveDroneLegacyHours}
          onSavePilotHours={legacyHours.savePilotLegacyHours}
        />

        {/* Maintenance Recalculate Dialog */}
        <MaintenanceRecalculateDialog
          open={maintenanceRecalculate.dialogOpen}
          onClose={maintenanceRecalculate.closeDialog}
          loading={maintenanceRecalculate.loading}
          recalculating={maintenanceRecalculate.recalculating}
          droneSerials={maintenanceRecalculate.droneSerials}
          selectedDrone={maintenanceRecalculate.selectedDrone}
          result={maintenanceRecalculate.result}
          error={maintenanceRecalculate.error}
          showDetails={maintenanceRecalculate.showDetails}
          onSelectDrone={maintenanceRecalculate.setSelectedDrone}
          onRecalculate={maintenanceRecalculate.recalculate}
          onToggleDetails={maintenanceRecalculate.toggleDetails}
        />

        {/* Tender Emails Dialog */}
        <TenderEmailsDialog
          open={tenderEmails.dialogOpen}
          onClose={tenderEmails.closeDialog}
          loading={tenderEmails.loading}
          emails={tenderEmails.emails}
          count={tenderEmails.count}
          error={tenderEmails.error}
          expandedId={tenderEmails.expandedId}
          reprocessingId={tenderEmails.reprocessingId}
          statusFilter={tenderEmails.statusFilter}
          onToggleExpanded={tenderEmails.toggleExpanded}
          onReprocess={tenderEmails.reprocessEmail}
          onStatusFilter={tenderEmails.setStatusFilter}
          onRefresh={tenderEmails.refreshEmails}
        />

        {/* Invite User Dialog */}
        <InviteUserDialog
          open={inviteUserDialogOpen}
          onClose={handleCloseInviteDialog}
          onInviteSent={handleInviteSent}
        />

        {/* Software Releases Dialog */}
        <SoftwareReleasesDialog
          open={softwareReleasesOpen}
          onClose={() => setSoftwareReleasesOpen(false)}
        />
      </Box>
    </Box>
  );
};
